import {
  auditPageSchema,
  credentialSummarySchema,
  enqueuedJobSchema,
  healthResponseSchema,
  jobFailurePageSchema,
  jobFailureSchema,
  memberSchema,
  queueStatsSchema,
  type AddMemberInput,
  type AuditPage,
  type AuditQuery,
  type CredentialSummary,
  type EnqueueDiagnosticsInput,
  type EnqueuedJob,
  type HealthResponse,
  type JobFailurePage,
  type JobFailureQuery,
  type JobFailureStatus,
  type JobFailureView,
  type Member,
  type MembershipRole,
  type QueueStats,
  type UpsertCredentialInput,
} from '@ai-sales/shared'
import { z } from 'zod'
import { apiRequest } from '../lib/api-client.js'

/**
 * Обращения к API воркспейса.
 *
 * Каждая функция принимает workspaceId первым аргументом и подставляет его
 * в путь. Идентификатор берётся из адреса страницы и никогда не из формы —
 * на сервере он всё равно сверяется с членством, но и здесь не стоит
 * заводить путь, по которому в запрос попадёт значение из поля ввода.
 */

const okSchema = z.object({ ok: z.literal(true) })

// ─── состояние ───

export async function fetchHealth(): Promise<HealthResponse> {
  return apiRequest('/health', healthResponseSchema)
}

// ─── участники ───

export async function fetchMembers(workspaceId: string): Promise<Member[]> {
  return apiRequest(`/workspaces/${workspaceId}/members`, z.array(memberSchema))
}

export async function addMember(workspaceId: string, input: AddMemberInput): Promise<Member> {
  return apiRequest(`/workspaces/${workspaceId}/members`, memberSchema, {
    method: 'POST',
    body: input,
  })
}

export async function updateMemberRole(
  workspaceId: string,
  membershipId: string,
  role: MembershipRole,
): Promise<Member> {
  return apiRequest(`/workspaces/${workspaceId}/members/${membershipId}`, memberSchema, {
    method: 'PATCH',
    body: { role },
  })
}

export async function removeMember(workspaceId: string, membershipId: string): Promise<void> {
  await apiRequest(`/workspaces/${workspaceId}/members/${membershipId}`, okSchema, {
    method: 'DELETE',
  })
}

// ─── учётные данные ───

export async function fetchCredentials(workspaceId: string): Promise<CredentialSummary[]> {
  return apiRequest(`/workspaces/${workspaceId}/credentials`, z.array(credentialSummarySchema))
}

export async function upsertCredential(
  workspaceId: string,
  input: UpsertCredentialInput,
): Promise<CredentialSummary> {
  return apiRequest(`/workspaces/${workspaceId}/credentials`, credentialSummarySchema, {
    method: 'PUT',
    body: input,
  })
}

export async function removeCredential(workspaceId: string, id: string): Promise<void> {
  await apiRequest(`/workspaces/${workspaceId}/credentials/${id}`, okSchema, { method: 'DELETE' })
}

// ─── журнал действий ───

export async function fetchAudit(workspaceId: string, query: AuditQuery): Promise<AuditPage> {
  return apiRequest(`/workspaces/${workspaceId}/audit`, auditPageSchema, { query })
}

// ─── задачи ───

export async function fetchQueueStats(workspaceId: string): Promise<QueueStats> {
  return apiRequest(`/workspaces/${workspaceId}/jobs/stats`, queueStatsSchema)
}

export async function fetchJobFailures(
  workspaceId: string,
  query: JobFailureQuery,
): Promise<JobFailurePage> {
  return apiRequest(`/workspaces/${workspaceId}/jobs/failures`, jobFailurePageSchema, { query })
}

export async function updateJobFailure(
  workspaceId: string,
  failureId: string,
  status: JobFailureStatus,
): Promise<JobFailureView> {
  return apiRequest(`/workspaces/${workspaceId}/jobs/failures/${failureId}`, jobFailureSchema, {
    method: 'PATCH',
    body: { status },
  })
}

export async function enqueueDiagnostics(
  workspaceId: string,
  input: EnqueueDiagnosticsInput,
): Promise<EnqueuedJob> {
  return apiRequest(`/workspaces/${workspaceId}/jobs/diagnostics`, enqueuedJobSchema, {
    method: 'POST',
    body: input,
  })
}
